import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaUserPlus } from 'react-icons/fa';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import { adminUserService } from '../../../services/adminUserService';
import { toastSuccess, toastError } from '../../../utils/toast';
import { useAuth } from '../../../context/AuthContext';

const ModalCrearUsuario = ({ isOpen, onClose, onCreated, plans }) => {
  const { currentUser } = useAuth();
  const [availableClients, setAvailableClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedAuthUserId, setSelectedAuthUserId] = useState('');
  const [planId, setPlanId] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setSelectedAuthUserId('');
    setPlanId('');
    setErrors({});
    setLoadingClients(true);
    adminUserService.getAvailableClients()
      .then(setAvailableClients)
      .catch(err => toastError(err.message || 'Error al cargar clientes'))
      .finally(() => setLoadingClients(false));
  }, [isOpen]);

  const clientOptions = availableClients.map(c => ({
    value: c.auth_user_id,
    label: `${c.full_name || '—'} (${c.email})`,
  }));

  const planOptions = (plans || []).map(p => ({ value: p.id, label: p.name }));

  const selectedClient = availableClients.find(c => c.auth_user_id === selectedAuthUserId);

  const validate = () => {
    const e = {};
    if (!selectedAuthUserId) e.client = 'Selecciona un usuario';
    if (!planId) e.plan = 'Selecciona un plan';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await adminUserService.registerClient({
        authUserId: selectedAuthUserId,
        planId,
        adminId: currentUser?.id,
        clientData: selectedClient,
      });
      toastSuccess(`${selectedClient?.full_name || 'Usuario'} registrado en el panel`);
      onCreated();
      onClose();
    } catch (err) {
      toastError(err.message || 'Error al registrar usuario');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-surface-card border border-white/[0.06] rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/[0.07]">
          <div className="flex items-center gap-2">
            <FaUserPlus style={{ color: '#3B82F6' }} />
            <h2 className="font-semibold" style={{ color: '#E8EAF0' }}>Registrar usuario en el panel</h2>
          </div>
          <button onClick={onClose} style={{ color: '#4A5270' }} className="hover:text-white transition-colors">
            <FaTimes size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {loadingClients ? (
            <div className="flex justify-center py-6" style={{ color: '#6B7391' }}>
              <FaSpinner className="animate-spin" size={24} />
            </div>
          ) : availableClients.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm" style={{ color: '#9BA3B8' }}>No hay clientes disponibles para registrar.</p>
              <p className="text-xs mt-1" style={{ color: '#6B7391' }}>
                Todos los clientes vinculados ya tienen configuración de plan.
              </p>
            </div>
          ) : (
            <>
              <Select
                label="Cliente *"
                options={clientOptions}
                value={selectedAuthUserId}
                onChange={e => {
                  setSelectedAuthUserId(e.target.value);
                  if (errors.client) setErrors(prev => ({ ...prev, client: '' }));
                }}
                placeholder="Buscar cliente..."
                error={errors.client}
                isSearchable={true}
                isClearable={false}
              />

              {selectedClient && (
                <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', color: '#93C5FD' }}>
                  <p className="font-medium">{selectedClient.full_name}</p>
                  <p className="text-xs" style={{ color: '#60A5FA' }}>{selectedClient.email}</p>
                  {selectedClient.whatsapp_number && (
                    <p className="text-xs" style={{ color: '#60A5FA' }}>{selectedClient.whatsapp_number}</p>
                  )}
                </div>
              )}

              <Select
                label="Plan inicial *"
                options={planOptions}
                value={planId}
                onChange={e => {
                  setPlanId(e.target.value);
                  if (errors.plan) setErrors(prev => ({ ...prev, plan: '' }));
                }}
                placeholder="Seleccionar plan..."
                error={errors.plan}
                isClearable={false}
              />
            </>
          )}
        </div>

        {/* Footer */}
        {availableClients.length > 0 && !loadingClients && (
          <div className="flex justify-end gap-3 p-5 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <FaSpinner className="animate-spin mr-2" /> : null}
              {saving ? 'Registrando...' : 'Registrar usuario'}
            </Button>
          </div>
        )}
        {availableClients.length === 0 && !loadingClients && (
          <div className="flex justify-end p-5 border-t">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalCrearUsuario;
